'use client'

import * as React from 'react'

interface CameraDimensions {
  width: number
  height: number
}

const DEFAULT_DIMENSIONS = {
  width: 640,
  height: 480,
}

export function useCameraDimensions(containerRef: React.RefObject<HTMLDivElement | null>): CameraDimensions {
  const [dimensions, setDimensions] = React.useState<CameraDimensions>(DEFAULT_DIMENSIONS)

  React.useEffect(() => {
    function calculateDimensions() {
      if (!containerRef.current) {
        setDimensions(DEFAULT_DIMENSIONS)
        return
      }

      const container = containerRef.current
      const containerWidth = container.clientWidth
      const containerHeight = container.clientHeight

      // If container dimensions are 0, use defaults
      if (containerWidth === 0 || containerHeight === 0) {
        setDimensions(DEFAULT_DIMENSIONS)
        return
      }

      // Default to 4:3 aspect ratio (most common for webcams)
      const aspectRatio = 4 / 3

      // Calculate dimensions while maintaining aspect ratio
      let width = Math.min(containerWidth, DEFAULT_DIMENSIONS.width)
      let height = width / aspectRatio

      // If height exceeds container or default, scale down
      const maxHeight = Math.min(containerHeight, DEFAULT_DIMENSIONS.height)
      if (height > maxHeight) {
        height = maxHeight
        width = height * aspectRatio
      }

      setDimensions({
        width: Math.floor(width),
        height: Math.floor(height),
      })
    }

    // Calculate initial dimensions using requestAnimationFrame to ensure DOM is ready
    const animationFrameId = requestAnimationFrame(calculateDimensions)

    // Debounced resize handler
    let resizeTimeout: NodeJS.Timeout
    const handleResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        calculateDimensions()
      }, 100) // Debounce resize calculations
    }

    window.addEventListener('resize', handleResize)

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize)
      clearTimeout(resizeTimeout)
      cancelAnimationFrame(animationFrameId)
    }
  }, [containerRef])

  return dimensions
}
